/** Lower case and no accents, so "Reply" finds "réply" and "Magpadala" finds "magpadalá". */
export function normalizeSearch(text: string): string {
  return text.normalize("NFD").replace(/\p{M}/gu, "").toLocaleLowerCase();
}

/** The words of a search box, normalised; blank when the box holds only spaces. */
export function searchTokens(query: string): string[] {
  return normalizeSearch(query).split(/\s+/).filter(Boolean);
}

/** What a step is known by: its label, and the other words and names that should find it. */
export interface SearchTerms {
  label: string;
  /** Localised keywords and the English aliases. */
  others: readonly string[];
}

/** Lower is better. */
const SCORE = {
  labelStart: 0,
  otherExact: 1,
  labelWordStart: 2,
  otherWordStart: 3,
  labelContains: 4,
  otherContains: 5,
} as const;
const NO_MATCH = Number.POSITIVE_INFINITY;

const wordsOf = (text: string): string[] => text.split(/[^\p{L}\p{N}]+/u).filter(Boolean);

function scoreToken(token: string, label: string, others: readonly string[]): number {
  if (label.startsWith(token)) return SCORE.labelStart;
  const otherWords = others.flatMap(wordsOf);
  if (others.includes(token) || otherWords.includes(token)) return SCORE.otherExact;
  if (wordsOf(label).some((word) => word.startsWith(token))) return SCORE.labelWordStart;
  if (otherWords.some((word) => word.startsWith(token))) return SCORE.otherWordStart;
  if (label.includes(token)) return SCORE.labelContains;
  if (others.some((other) => other.includes(token))) return SCORE.otherContains;
  return NO_MATCH;
}

/**
 * How well a step answers the search: every word of the search must be found in the label or in the other names, and the
 * score adds up the quality of each find (a label that starts with the word beats a keyword that merely contains it).
 * `null` when a word is not found anywhere.
 */
export function searchScore(tokens: readonly string[], terms: SearchTerms): number | null {
  const label = normalizeSearch(terms.label);
  const others = terms.others.map(normalizeSearch).filter(Boolean);
  let total = 0;
  for (const token of tokens) {
    const score = scoreToken(token, label, others);
    if (score === NO_MATCH) return null;
    total += score;
  }
  return total;
}

/** Keywords as stored in the catalogs: one comma-separated string. */
export function splitKeywords(raw: string): string[] {
  return raw.split(",").map((word) => word.trim()).filter(Boolean);
}
